import { useCallback } from "react";
import {
    AccrualReviewApproveResponse,
    ApproveAccrualReviewBody,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { ScheduleMatchResolution } from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import {
    ApproveWithScheduleResolutionsOutcome,
    approveAccrualReviewRequest,
    useApproveWithScheduleResolutions,
} from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import { hasSelectionLeftAfterUnmatch } from "../schedule/matchResolutions";
import { MatchableItem } from "../types";

export interface AllocationApproval {
    body: ApproveAccrualReviewBody;
    hasRemainingAllocations: boolean;
    selectedItems: MatchableItem[];
}

export function useApproveAllocationEntry() {
    const { approveWithScheduleResolutions, scheduleResolutionModals } =
        useApproveWithScheduleResolutions();

    const approveAllocation = useCallback(
        ({
            workspaceId,
            approval,
            onApproved,
            onUnmatched,
        }: {
            workspaceId: string;
            approval: AllocationApproval;
            onApproved: (response: AccrualReviewApproveResponse) => void;
            onUnmatched?: (resolutions: ScheduleMatchResolution[]) => void;
        }): Promise<ApproveWithScheduleResolutionsOutcome> =>
            approveWithScheduleResolutions({
                request: approveAccrualReviewRequest({
                    workspaceId,
                    body: approval.body,
                }),
                hasWorkLeft: (resolutions) =>
                    hasSelectionLeftAfterUnmatch({
                        selectedItems: approval.selectedItems,
                        resolutions,
                    }) || approval.hasRemainingAllocations,
                onApproved,
                onUnmatched,
            }),
        [approveWithScheduleResolutions],
    );

    return { approveAllocation, scheduleResolutionModals };
}
